import { z } from "zod";

import { isValidUSPhoneNumber } from "@/app/_module/utils/formatters";

// Beneficiary schema
export const beneficiarySchema = z.object({
  id: z.string().optional(), // For existing beneficiaries
  name: z.string().min(1, { message: "Beneficiary name is required" }),
  relationship: z.string().optional().or(z.literal("")),
  percentage: z.number().min(0).max(100).optional(),
  dob: z.string().optional().or(z.literal("")),
  ssn: z.string().optional().or(z.literal("")),
  // Contingent beneficiary fields
  contingentName: z.string().optional().or(z.literal("")),
  contingentRelationship: z.string().optional().or(z.literal("")),
  contingentPercentage: z.number().min(0).max(100).optional(),
});

// Base lead schema matching your actual Lead interface
export const leadSchema = z.object({
  // Required fields
  id: z.string(),
  userId: z.string(),
  
  // Optional fields from your Lead interface
  name: z.string().min(2, { message: "Name must be at least 2 characters long" }).max(100, { message: "Name cannot exceed 100 characters" }).optional().or(z.literal("")),
  assigned: z.string().max(100, { message: "Assigned cannot exceed 100 characters" }).optional().or(z.literal("")),
  leadStat: z.string().optional().or(z.literal("")),
  marital: z.enum(['Single', 'Married', 'Divorced', 'Widowed', 'Other'], { 
    message: "Marital status must be one of: Single, Married, Divorced, Widowed, Other" 
  }).optional().or(z.literal("")),
  license: z.string().max(50, { message: "License cannot exceed 50 characters" }).optional().or(z.literal("")),
  employmentStatus: z.enum(['Employed', 'Self-employed', 'Unemployed', 'Retired', 'Student', 'Other'], { 
    message: "Employment status must be one of: Employed, Self-employed, Unemployed, Retired, Student, Other" 
  }).optional().or(z.literal("")),
  earned: z.string().max(50, { message: "Earned income cannot exceed 50 characters" }).optional().or(z.literal("")),
  height: z.string().max(20, { message: "Height cannot exceed 20 characters" }).optional().or(z.literal("")),
  weight: z.string().max(20, { message: "Weight cannot exceed 20 characters" }).optional().or(z.literal("")),
  unearned: z.string().max(50, { message: "Unearned income cannot exceed 50 characters" }).optional().or(z.literal("")),
  worth: z.string().max(50, { message: "Net worth cannot exceed 50 characters" }).optional().or(z.literal("")),
  age: z.string().max(10, { message: "Age cannot exceed 10 characters" }).optional().or(z.literal("")),
  birthday: z.string().max(20, { message: "Birthday cannot exceed 20 characters" }).optional().or(z.literal("")),
  email: z.string().email({ message: "Please provide a valid email address" }).optional().or(z.literal("")),
  phone: z.string()
    .refine((val) => {
      if (!val || val === "") return true; // Allow empty
      const cleaned = val.replace(/\D/g, '');
      // Allow 10 digits (US) or 11 digits (with country code)
      return cleaned.length === 10 || cleaned.length === 11;
    }, "Phone number must be 10 or 11 digits")
    .optional().or(z.literal("")),
  lastCall: z.string().max(100, { message: "Last call cannot exceed 100 characters" }).optional().or(z.literal("")),
  longestCall: z.string().max(100, { message: "Longest call cannot exceed 100 characters" }).optional().or(z.literal("")),
  lastCallAt: z.date().optional(),
  lastLongestCallAt: z.date().optional(),
  dob: z.string().max(20, { message: "Date of birth cannot exceed 20 characters" }).optional().or(z.literal("")),
  gender: z.enum(['Male', 'Female', 'Other', 'Prefer not to say'], { 
    message: "Gender must be one of: Male, Female, Other, Prefer not to say" 
  }).optional().or(z.literal("")),
  uwrDate: z.date().optional(),
  emailVerifiedAt: z.date().optional(),
  phoneVerifiedAt: z.date().optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  // Custom fields support
  customFields: z.record(z.string(), z.any()).optional(),
  // Beneficiaries
  beneficiaries: z.array(beneficiarySchema).optional(),
});

// Schema for updating leads (all fields optional except id)
export const updateLeadSchema = z.object({
  name: z
    .string()
    .min(2, { message: "Name must be at least 2 characters long" })
    .max(100, { message: "Name cannot exceed 100 characters" })
    .optional()
    .or(z.literal("")),
  phone: z.string()
    .refine((val) => {
      if (!val || val === "") return true; // Allow empty
      const cleaned = val.replace(/\D/g, '');
      // Allow 10 digits (US) or 11 digits (with country code)
      return cleaned.length === 10 || cleaned.length === 11;
    }, "Phone number must be 10 or 11 digits")
    .optional().or(z.literal("")),
  assigned: z.string().max(100, { message: "Assigned cannot exceed 100 characters" }).optional().or(z.literal("")),
  leadStat: z.string().optional().or(z.literal("")),
  marital: z.enum(['Single', 'Married', 'Divorced', 'Widowed', 'Other'], {
    message: "Marital status must be one of: Single, Married, Divorced, Widowed, Other"
  }).optional().or(z.literal("")),
  relationship: z.string().max(50, { message: "Relationship cannot exceed 50 characters" }).optional().or(z.literal("")),
  percentage: z.string().max(10, { message: "Percentage cannot exceed 10 characters" }).optional().or(z.literal("")),
  license: z.string().max(50, { message: "License cannot exceed 50 characters" }).optional().or(z.literal("")),
  employmentStatus: z.enum(['Employed', 'Self-employed', 'Unemployed', 'Retired', 'Student', 'Other'], { 
    message: "Employment status must be one of: Employed, Self-employed, Unemployed, Retired, Student, Other" 
  }).optional().or(z.literal("")),
  earned: z.string().max(50, { message: "Earned income cannot exceed 50 characters" }).optional().or(z.literal("")),
  height: z.string().max(20, { message: "Height cannot exceed 20 characters" }).optional().or(z.literal("")),
  weight: z.string().max(20, { message: "Weight cannot exceed 20 characters" }).optional().or(z.literal("")),
  unearned: z.string().max(50, { message: "Unearned income cannot exceed 50 characters" }).optional().or(z.literal("")),
  worth: z.string().max(50, { message: "Net worth cannot exceed 50 characters" }).optional().or(z.literal("")),
  email: z.string().email({ message: "Please provide a valid email address" }).optional().or(z.literal("")),
  dob: z.string().max(20, { message: "Date of birth cannot exceed 20 characters" }).optional().or(z.literal("")),
  gender: z.enum(['Male', 'Female', 'Other', 'Prefer not to say'], {
    message: "Gender must be one of: Male, Female, Other, Prefer not to say"
  }).optional().or(z.literal("")),
  address: z.string().max(200, { message: "Address cannot exceed 200 characters" }).optional().or(z.literal("")),
  city: z.string().max(100, { message: "City cannot exceed 100 characters" }).optional().or(z.literal("")),
  state: z.string().max(50, { message: "State cannot exceed 50 characters" }).optional().or(z.literal("")),
  zipCode: z.string().max(10, { message: "Zip code cannot exceed 10 characters" }).optional().or(z.literal("")),
  notes: z.string().max(5000, { message: "Notes cannot exceed 5000 characters" }).optional().or(z.literal("")),
  // Age is calculated from dob, not stored
  // Custom fields support
  customFields: z.record(z.string(), z.any()).optional(),
  // Beneficiaries - added to update schema
  beneficiaries: z.array(beneficiarySchema).optional(),
});

// Simplified schema for creating leads (only name required)
export const createLeadSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters long" }).max(100, { message: "Name cannot exceed 100 characters" }),
  phone: z.string()
    .refine((val) => {
      if (!val || val === "") return true; // Allow empty
      const cleaned = val.replace(/\D/g, '');
      return cleaned.length === 10 || cleaned.length === 11;
    }, "Phone number must be 10 or 11 digits")
    .optional().or(z.literal("")),
  leadStat: z.string().optional().or(z.literal("")),
});

export type BeneficiarySchema = z.infer<typeof beneficiarySchema>;
export type LeadSchema = z.infer<typeof leadSchema>;
export type UpdateLeadSchema = z.infer<typeof updateLeadSchema>;
export type CreateLeadSchema = z.infer<typeof createLeadSchema>;